/**
 * Module Context - Provides current module state based on URL path.
 * Used to determine which module's navigation and AppBar color to show.
 */

import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useGuardedNavigate } from '../hooks/useGuardedNavigate';
import {
  ModuleConfig,
  getModuleByPath,
  getModuleById,
  getAccessibleModules,
  getEffectiveEnabledModules,
  isModuleEnabled,
  MODULES,
  MODULE_PICKER_MODULES,
  PINNED_MODULES,
  ROLE_BASED_MODULE_IDS,
  ALWAYS_AVAILABLE_MODULE_IDS,
} from '../config/modules.config';
import { useAuth, hasDeveloperPortalAccess } from '../auth';
import { useModuleAdminCheck } from '../hooks/useIsModuleAdmin';
import { useModuleName } from '../hooks/useModuleName';

interface ModuleContextValue {
  /** The currently active module based on URL path */
  currentModule: ModuleConfig;
  /**
   * The current module's persona-aware display name (e.g. "Freight Operations"
   * rather than "Shipments"). Resolved once here so consumers that only need the
   * name — notably `usePageTitle`, which `PageHeader` calls on nearly every page —
   * don't each have to take a `usePersona` (and therefore `useAuth`/react-query)
   * dependency. `ModuleProvider` already sits inside those providers.
   */
  currentModuleName: string;
  /** All modules (unfiltered) */
  allModules: ModuleConfig[];
  /** Modules accessible to the current user (role-based and settings-based) */
  accessibleModules: ModuleConfig[];
  /** Modules visible but disabled due to tenant/user settings */
  disabledModules: ModuleConfig[];
  /** Modules that should appear in the module picker (accessible + showInModulePicker) */
  pickerModules: ModuleConfig[];
  /** Modules in picker that are disabled due to tenant/user settings */
  disabledPickerModules: ModuleConfig[];
  /** Utility modules pinned at the bottom of the module picker */
  pinnedModules: ModuleConfig[];
  /** Navigate to a different module */
  switchModule: (moduleId: string) => void;
  /** Check if a module is the current one */
  isCurrentModule: (moduleId: string) => boolean;
  /** Check if a module is enabled (not disabled by settings) */
  isModuleEnabledForUser: (moduleId: string) => boolean;
}

/**
 * Unified module color for sidebar/header theming.
 * When set, every module's `currentModule.color` is overridden with this value,
 * so the sidebar, header, notification bells, etc. render neutrally regardless
 * of which module is active. The module picker still iterates over real
 * per-module colors (it reads `module.color` directly).
 *
 * `null` (current) = per-module theming: each module's own hue flows into the
 * shell tint, the `--accent-module` token, and everything downstream of it.
 * Set to a hex to force the whole app back to one neutral chrome.
 */
const UNIFIED_MODULE_COLOR: string | null = null;

export const ModuleContext = createContext<ModuleContextValue | null>(null);

interface ModuleProviderProps {
  children: React.ReactNode;
  /** Show developer modules (default: check NODE_ENV) */
  showDevModules?: boolean;
}

export function ModuleProvider({ children, showDevModules }: ModuleProviderProps) {
  const location = useLocation();
  // Switching module is a sidebar/module-picker click on a `button`, so it is
  // invisible to the anchor interceptor in useUnsavedChangesWarning the same way a
  // nav entry is — and it discards the same dirty form. Guard it at the one place
  // that navigates.
  const navigate = useGuardedNavigate();
  const { user } = useAuth();

  const isAdmin = user?.is_admin === true;
  const isSysadmin = user?.is_sysadmin === true;
  // Mirrors require_module_admin() for a whole-module gate (`requiresModuleAdmin`)
  // — a module whose entire API surface is module-admin-gated is not offered to
  // someone the backend would only ever 403.
  const isModuleAdmin = useModuleAdminCheck();
  const showDev = showDevModules ?? import.meta.env.DEV;

  // Developer Portal is a deliberate special case: the `developer` module is
  // status:hidden (so it's filtered out of getAccessibleModules → never a
  // homepage card) and has no tenant-wide toggle. Admins/sysadmins have implicit
  // access; non-admins are granted per-user. When the user has access we inject
  // it into the module picker only (below), never into the homepage list.
  // Shared helper keeps this identical to DeveloperRoute + the user-menu item.
  const hasDeveloperAccess = hasDeveloperPortalAccess(user);

  // Platform-available modules (sysadmin level) - modules not in this list are completely hidden
  const platformAvailableModules = user?.platform_available_modules ?? null;

  // Module ceiling (set by admin/inviter) — modules above ceiling are hidden entirely
  const moduleCeiling = user?.module_ceiling ?? null;

  // Tenant+user enabled modules - modules not in this list appear greyed out
  // Note: ceiling is handled separately as a hard filter (hidden, not greyed out)
  const tenantUserEnabledModules = useMemo(
    () => getEffectiveEnabledModules(
      null, // Don't include platform here - handled separately
      user?.tenant_enabled_modules,
      user?.enabled_modules
    ),
    [user?.tenant_enabled_modules, user?.enabled_modules]
  );

  const currentModule = useMemo(
    () => {
      const module = getModuleByPath(location.pathname);
      if (UNIFIED_MODULE_COLOR === null) return module;
      return { ...module, color: UNIFIED_MODULE_COLOR };
    },
    [location.pathname]
  );

  const getModuleName = useModuleName();
  const currentModuleName = getModuleName(currentModule);

  // First filter by role-based access
  const roleAccessibleModules = useMemo(
    () => getAccessibleModules(isAdmin, isSysadmin, isModuleAdmin, showDev),
    [isAdmin, isSysadmin, isModuleAdmin, showDev]
  );

  // Check if a module is available at platform level (sysadmin)
  const isPlatformAvailable = useCallback(
    (moduleId: string): boolean => {
      // Always-available modules (e.g., home) cannot be hidden by platform settings
      if (ALWAYS_AVAILABLE_MODULE_IDS.includes(moduleId)) return true;
      // Role-based modules (admin, sysadmin, devOnly) are always available at platform level
      // They're controlled by role-based access, not platform settings
      if (ROLE_BASED_MODULE_IDS.includes(moduleId)) return true;
      // If platform has no restrictions, all modules are available
      if (platformAvailableModules === null) return true;
      return platformAvailableModules.includes(moduleId);
    },
    [platformAvailableModules]
  );

  // Check if a module is within the user's ceiling (set by admin/inviter)
  // Modules above ceiling are completely hidden (like platform-unavailable)
  const isWithinCeiling = useCallback(
    (moduleId: string): boolean => {
      // Always-available and role-based modules are never affected by ceiling
      if (ALWAYS_AVAILABLE_MODULE_IDS.includes(moduleId)) return true;
      if (ROLE_BASED_MODULE_IDS.includes(moduleId)) return true;
      // If no ceiling, all modules are available
      if (moduleCeiling === null) return true;
      return moduleCeiling.includes(moduleId);
    },
    [moduleCeiling]
  );

  // Then filter by platform availability and tenant/user enablement
  const { accessibleModules, disabledModules, pickerModules, disabledPickerModules, pinnedModules } = useMemo(() => {
    const enabled: ModuleConfig[] = [];
    const disabled: ModuleConfig[] = [];

    for (const module of roleAccessibleModules) {
      // Platform-disabled modules are completely hidden
      if (!isPlatformAvailable(module.id)) continue;
      // Above-ceiling modules are completely hidden
      if (!isWithinCeiling(module.id)) continue;

      if (isModuleEnabled(module.id, tenantUserEnabledModules)) {
        enabled.push(module);
      } else {
        // Tenant/user disabled - show greyed out
        disabled.push(module);
      }
    }

    // Filter picker modules: only those with showInModulePicker=true that pass role-based access
    const roleAccessibleIds = new Set(roleAccessibleModules.map(m => m.id));
    const pickerEnabled: ModuleConfig[] = [];
    const pickerDisabled: ModuleConfig[] = [];

    for (const module of MODULE_PICKER_MODULES) {
      if (!roleAccessibleIds.has(module.id)) continue; // Skip if role-based access fails
      // Platform-disabled modules are completely hidden
      if (!isPlatformAvailable(module.id)) continue;
      // Above-ceiling modules are completely hidden
      if (!isWithinCeiling(module.id)) continue;

      if (isModuleEnabled(module.id, tenantUserEnabledModules)) {
        pickerEnabled.push(module);
      } else {
        // Tenant/user disabled - show greyed out
        pickerDisabled.push(module);
      }
    }

    // Filter pinned modules (utility modules like Files, Mail). These are now
    // toggleable, so a tenant/user that turns one off should see it drop from
    // the picker entirely (re-enable via Settings → Modules), not greyed out.
    const pinnedFiltered: ModuleConfig[] = [];
    for (const module of PINNED_MODULES) {
      if (!roleAccessibleIds.has(module.id)) continue;
      if (!isPlatformAvailable(module.id)) continue;
      if (!isWithinCeiling(module.id)) continue;
      if (!isModuleEnabled(module.id, tenantUserEnabledModules)) continue;
      pinnedFiltered.push(module);
    }

    // Inject the Developer Portal into the picker (only) when the user has access.
    // It's status:hidden so it never reaches getAccessibleModules/the homepage; we
    // surface it here as a normal picker entry without going through the
    // platform/tenant/ceiling filters (it has no tenant-wide toggle by design).
    if (hasDeveloperAccess) {
      const developerModule = getModuleById('developer');
      if (developerModule && !pickerEnabled.some(m => m.id === developerModule.id)) {
        pickerEnabled.push(developerModule);
      }
    }

    return {
      accessibleModules: enabled,
      disabledModules: disabled,
      pickerModules: pickerEnabled,
      disabledPickerModules: pickerDisabled,
      pinnedModules: pinnedFiltered,
    };
  }, [roleAccessibleModules, tenantUserEnabledModules, isPlatformAvailable, isWithinCeiling, hasDeveloperAccess]);

  const switchModule = useCallback(
    (moduleId: string) => {
      const module = getModuleById(moduleId);
      if (module) {
        navigate(module.defaultPath);
      }
    },
    [navigate]
  );

  const isCurrentModule = useCallback(
    (moduleId: string) => currentModule.id === moduleId,
    [currentModule.id]
  );

  const isModuleEnabledForUser = useCallback(
    (moduleId: string) => isPlatformAvailable(moduleId) && isModuleEnabled(moduleId, tenantUserEnabledModules),
    [isPlatformAvailable, tenantUserEnabledModules]
  );

  const value = useMemo(
    () => ({
      currentModule,
      currentModuleName,
      allModules: MODULES,
      accessibleModules,
      disabledModules,
      pickerModules,
      disabledPickerModules,
      pinnedModules,
      switchModule,
      isCurrentModule,
      isModuleEnabledForUser,
    }),
    [currentModule, currentModuleName, accessibleModules, disabledModules, pickerModules, disabledPickerModules, pinnedModules, switchModule, isCurrentModule, isModuleEnabledForUser]
  );

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
}

export function useModule(): ModuleContextValue {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModule must be used within a ModuleProvider');
  }
  return context;
}
